import { Row, Col, Statistic, Flex } from 'antd';
import { useTranslation } from 'translations/hooks';

import { useLegendFilter } from 'business/production-and-performance/pages/excavation-build-time/providers/legend-filter';
import { useProdAndPerfFilters } from 'business/production-and-performance/providers/prod-and-perf-filters-provider';
import { parseAndFormatRingDuration } from 'business/production-and-performance/services/ring-duration';
import { GetExcavationAndBuildingDurationResponseDto } from 'generated/apiSchemas';
import { useMediaType } from 'technical/media/hooks';
import { ContentTitle } from 'ui/content-title';
import { ShowMoreToggle } from 'ui/show-more-button';
import { useShowMore } from 'ui/show-more-button/hook';
import { ShowMoreContainer } from 'ui/show-more-button/show-more-container';

const COL_PROPS = {
  xs: 12,
  sm: 12,
  md: 12,
  lg: 12,
  xl: 6,
};

interface Props {
  data: GetExcavationAndBuildingDurationResponseDto['averages'];
}

export const ExcavationBuildTimeAverages = ({ data }: Props) => {
  const { t } = useTranslation();
  const {
    legendFilter: {
      realExcavationDuration,
      realBuildDuration,
      totalExcavationDuration,
      totalBuildDuration,
    },
  } = useLegendFilter();
  const filters = useProdAndPerfFilters();
  const { isMobile } = useMediaType();
  const { showMore, toggleShowMore } = useShowMore({ averages: false });

  const showTotalDurations = filters?.showTotalDurations;

  return (
    <Flex vertical>
      {isMobile ? <ContentTitle>{t('common.averages')}</ContentTitle> : null}

      <ShowMoreContainer
        enableToggle={isMobile}
        toggleState={showMore.averages}
      >
        <Row gutter={8}>
          <Col {...COL_PROPS}>
            {realExcavationDuration ? (
              <Statistic
                title={t(
                  'productionAndPerformance.excavationBuildTime.averages.avgRealExcavationDuration',
                )}
                value={parseAndFormatRingDuration(
                  data.avgRealExcavationDuration,
                )}
              />
            ) : null}
          </Col>
          <Col {...COL_PROPS}>
            {realBuildDuration ? (
              <Statistic
                title={t(
                  'productionAndPerformance.excavationBuildTime.averages.avgRealBuildDuration',
                )}
                value={parseAndFormatRingDuration(data.avgRealBuildDuration)}
              />
            ) : null}
          </Col>
          {showTotalDurations ? (
            <>
              <Col {...COL_PROPS}>
                {totalExcavationDuration ? (
                  <Statistic
                    title={t(
                      'productionAndPerformance.excavationBuildTime.averages.avgTotalExcavationDuration',
                    )}
                    value={parseAndFormatRingDuration(
                      data.avgTotalExcavationDuration,
                    )}
                  />
                ) : null}
              </Col>
              <Col {...COL_PROPS}>
                {totalBuildDuration ? (
                  <Statistic
                    title={t(
                      'productionAndPerformance.excavationBuildTime.averages.avgTotalBuildDuration',
                    )}
                    value={parseAndFormatRingDuration(
                      data.avgTotalBuildDuration,
                    )}
                  />
                ) : null}
              </Col>
            </>
          ) : null}
        </Row>
      </ShowMoreContainer>
      <ShowMoreToggle
        enableToggle={isMobile}
        toggleState={showMore.averages}
        toggleFunc={toggleShowMore('averages')}
      />
    </Flex>
  );
};
